import React, { useState, useCallback } from 'react';
import AppModal from '../AppModal';
import { Spin, Form, Input, InputNumber, Select, message } from 'antd';
import { PlusOutlined, DollarOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import Table from '@/components/ui/table/Table';
import { useProjectPayments } from '@hooks/projects/useProjectPayments';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ContentCard,
    MutedText,
} from '../style';

export default function ProjectPaymentsTab({ projectId }) {
    const {
        canView,
        canCreate,
        payments,
        isLoading,
        summary,
        totalDisplay,
        installmentsLabel,
        paymentTableColumns,
        tableData,
        currencyOptions,
        submitPayment,
        createPaymentPending,
    } = useProjectPayments(projectId);
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();

    const closeModal = useCallback(() => {
        setModalOpen(false);
        form.resetFields();
    }, [form]);

    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            submitPayment(values, {
                onValidationError: (err) => message.warning(err),
                onSuccess: () => {
                    message.success('Payment recorded');
                    closeModal();
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to record payment'),
            });
        });
    }, [form, submitPayment, closeModal]);

    const addButton = canCreate ? (
        <Button
            type="button"
            $primary
            $blackOnLight
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            $size="13px"
            $padding="8px 16px"
        >
            Add payment
        </Button>
    ) : null;

    if (!canView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <DollarOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>You don&apos;t have permission to view payments</EmptyStateTitle>
                <EmptyStateDescription>Contact your admin if you need access.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }

    return (
        <>
            <Container $display="flex" $flexDirection="column" $gap="20px" $width="100%">
                <Container $display="flex" $flexWrap="wrap" $gap="16px" $marginBottom="20px">
                    <ContentCard $padding="16px" style={{ minWidth: 120 }}>
                        <MutedText style={{ margin: 0, fontSize: 12 }}>Total received</MutedText>
                        <span style={{ fontSize: 24, fontWeight: 600 }}>{totalDisplay}</span>
                    </ContentCard>
                    <ContentCard $padding="16px" style={{ minWidth: 120 }}>
                        <MutedText style={{ margin: 0, fontSize: 12 }}>Installments</MutedText>
                        <span style={{ fontSize: 24, fontWeight: 600 }}>{summary.installmentsCount}</span>
                    </ContentCard>
                </Container>

                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="center"
                    $flexWrap="wrap"
                    $gap="12px"
                    $marginBottom="8px"
                >
                    <MutedText style={{ margin: 0, fontSize: 13 }}>Payment history</MutedText>
                    {addButton}
                </Container>

                {payments.length === 0 ? (
                    <EmptyStateBlock>
                        <EmptyStateIcon>
                            <DollarOutlined />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No payments yet</EmptyStateTitle>
                        <EmptyStateDescription>Record installments using the button above.</EmptyStateDescription>
                        {canCreate && <div style={{ marginTop: 16 }}>{addButton}</div>}
                    </EmptyStateBlock>
                ) : (
                    <Table
                        title=""
                        columns={paymentTableColumns}
                        dataSource={tableData}
                        hideToolbar
                        showSearch={false}
                        showFilter={false}
                        showMore={false}
                        pagination
                        pageSize={10}
                        $topMargin={false}
                        $radius={12}
                    />
                )}
            </Container>

            <AppModal size="md" title="Add payment" open={modalOpen} onCancel={closeModal} footer={null}>
                <CreateRoleModalBody>
                    <Form form={form} layout="vertical">
                        <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Enter amount' }]}>
                            <InputNumber
                                min={0.01}
                                step={100}
                                style={{ width: '100%' }}
                                placeholder="e.g. 15000"
                                size="large"
                            />
                        </Form.Item>
                        <Form.Item
                            name="currency"
                            label="Currency"
                            initialValue="USD"
                            rules={[{ required: true, message: 'Select currency' }]}
                        >
                            <Select options={currencyOptions} size="large" placeholder="Currency" />
                        </Form.Item>
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea rows={3} placeholder="e.g. First installment" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="14px" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button $primary $blackOnLight $size="14px" loading={createPaymentPending} onClick={handleSubmit}>
                        Add payment
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </>
    );
}
